import { useQuery } from '@apollo/client';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { PageTitle } from 'components/PageTitle/PageTitle';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENTITIES, ENTITY_TYPES, LOCATIONS, RESERVATIONS } from 'services/reservations';

interface EntityType {
    id: string;
    name: string;
    slug: string;
    color: string;
}

interface Location {
    id: string;
    name: string;
}

interface Entity {
    id: string;
    name: string;
    capacity: number | null;
    entityType: EntityType;
    location: Location;
}

interface Reservation {
    id: string;
    startDatetime: string;
    endDatetime: string;
    status: string;
    customerName: string;
    entity: {
        id: string;
    };
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8 AM to 9 PM

export const AvailabilityGrid: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [filterEntityTypeId, setFilterEntityTypeId] = useState('');
    const [filterLocationId, setFilterLocationId] = useState('');

    const { t } = useTranslation('translation');

    const fromDate = dayjs(selectedDate).startOf('day').toISOString();
    const toDate = dayjs(selectedDate).endOf('day').toISOString();

    const { data: entitiesData, loading: loadingEntities } = useQuery(ENTITIES, {
        variables: {
            activeOnly: true,
            entityTypeId: filterEntityTypeId || null,
            locationId: filterLocationId || null,
        },
    });

    const { data: reservationsData, loading: loadingReservations } = useQuery(RESERVATIONS, {
        variables: { fromDate, toDate },
    });

    const { data: entityTypesData } = useQuery(ENTITY_TYPES);
    const { data: locationsData } = useQuery(LOCATIONS);

    if (loadingEntities || loadingReservations) return <Loader />;

    const entities: Entity[] = entitiesData?.entities?.edges?.map((e: any) => e.node) || [];
    const reservations: Reservation[] =
        reservationsData?.reservations?.edges?.map((e: any) => e.node) || [];
    const entityTypes = entityTypesData?.entityTypes?.edges?.map((e: any) => e.node) || [];
    const locations = locationsData?.locations?.edges?.map((e: any) => e.node) || [];

    const getSlotStatus = (entityId: string, hour: number, minute: number) => {
        const slotStart = dayjs(`${selectedDate} ${hour}:${minute.toString().padStart(2, '0')}`);
        const slotEnd = slotStart.add(30, 'minute');

        for (const res of reservations) {
            if (res.entity.id !== entityId) continue;
            if (res.status === 'cancelled') continue;

            const resStart = dayjs(res.startDatetime);
            const resEnd = dayjs(res.endDatetime);

            if (slotStart.isBefore(resEnd) && slotEnd.isAfter(resStart)) {
                return {
                    status: res.status,
                    reservation: res,
                };
            }
        }

        return { status: 'available', reservation: null };
    };

    const getStatusClass = (status: string) => {
        switch (status) {
            case 'confirmed':
                return 'bg-red-100 border-red-300';
            case 'pending':
                return 'bg-yellow-100 border-yellow-300';
            case 'available':
                return 'bg-green-50 hover:bg-green-100';
            default:
                return 'bg-gray-100';
        }
    };

    // Calculate utilization stats
    const totalSlots = entities.length * HOURS.length * 2; // 2 slots per hour
    let bookedSlots = 0;
    entities.forEach((entity) => {
        HOURS.forEach((hour) => {
            [0, 30].forEach((minute) => {
                const { status } = getSlotStatus(entity.id, hour, minute);
                if (status !== 'available') bookedSlots++;
            });
        });
    });
    const utilizationPercent = totalSlots > 0 ? Math.round((bookedSlots / totalSlots) * 100) : 0;

    return (
        <Container>
            <div className="flex justify-between items-center mb-6">
                <PageTitle>{t('reservations.availabilityGrid')}</PageTitle>
                <div className="flex gap-4 items-center">
                    <button
                        onClick={() => setSelectedDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD'))}
                        className="p-2 hover:bg-gray-100 rounded"
                    >
                        ←
                    </button>
                    <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    />
                    <button
                        onClick={() => setSelectedDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD'))}
                        className="p-2 hover:bg-gray-100 rounded"
                    >
                        →
                    </button>
                    <Button onClick={() => setSelectedDate(dayjs().format('YYYY-MM-DD'))}>{t('reservations.today')}</Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">{t('reservations.date')}</p>
                    <p className="text-xl font-semibold">{dayjs(selectedDate).format('ddd, MMM D')}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">{t('reservations.totalEntities')}</p>
                    <p className="text-xl font-semibold">{entities.length}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">{t('reservations.reservationsCount')}</p>
                    <p className="text-xl font-semibold">
                        {reservations.filter((r) => r.status !== 'cancelled').length}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-sm text-gray-500">{t('reservations.utilization')}</p>
                    <p className="text-xl font-semibold">{utilizationPercent}%</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 mb-4">
                <select
                    className="border rounded px-3 py-2"
                    value={filterEntityTypeId}
                    onChange={(e) => setFilterEntityTypeId(e.target.value)}
                >
                    <option value="">{t('reservations.allTypes')}</option>
                    {entityTypes.map((type: EntityType) => (
                        <option key={type.id} value={type.id}>
                            {type.name}
                        </option>
                    ))}
                </select>
                <select
                    className="border rounded px-3 py-2"
                    value={filterLocationId}
                    onChange={(e) => setFilterLocationId(e.target.value)}
                >
                    <option value="">{t('reservations.allLocations')}</option>
                    {locations.map((loc: Location) => (
                        <option key={loc.id} value={loc.id}>
                            {loc.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Legend */}
            <div className="flex gap-6 mb-4 text-sm">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-50 border border-green-200" />
                    <span>{t('reservations.available')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-100 border border-red-300" />
                    <span>{t('reservations.confirmed')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300" />
                    <span>{t('reservations.pending')}</span>
                </div>
            </div>

            {/* Grid */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full border-collapse min-w-max">
                    <thead>
                        <tr>
                            <th className="sticky left-0 bg-gray-50 border-b border-r px-4 py-3 text-left font-medium text-sm">
                                {t('reservations.entity')}
                            </th>
                            {HOURS.map((hour) => (
                                <th
                                    key={hour}
                                    colSpan={2}
                                    className="border-b px-2 py-3 text-center font-medium text-sm min-w-[80px]"
                                >
                                    {hour}:00
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {entities.map((entity) => (
                            <tr key={entity.id} className="border-b">
                                <td className="sticky left-0 bg-white border-r px-4 py-2">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="w-2 h-2 rounded-full"
                                            style={{ backgroundColor: entity.entityType.color }}
                                        />
                                        <div>
                                            <p className="font-medium text-sm">{entity.name}</p>
                                            <p className="text-xs text-gray-500">{entity.location.name}</p>
                                        </div>
                                    </div>
                                </td>
                                {HOURS.map((hour) =>
                                    [0, 30].map((minute) => {
                                        const { status, reservation } = getSlotStatus(entity.id, hour, minute);
                                        return (
                                            <td
                                                key={`${hour}:${minute}`}
                                                className={`border-r border-gray-100 p-0.5 ${getStatusClass(status)}`}
                                                title={
                                                    reservation
                                                        ? `${reservation.customerName} (${dayjs(reservation.startDatetime).format('HH:mm')} - ${dayjs(reservation.endDatetime).format('HH:mm')})`
                                                        : `${t('reservations.available')}: ${hour}:${minute.toString().padStart(2, '0')}`
                                                }
                                            >
                                                <div className="h-8 w-full" />
                                            </td>
                                        );
                                    })
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Container>
    );
};
